import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useEffect, useRef, useState } from "react";

import { Select2ApplyBar } from "./Select2ApplyBar";

export interface Select2TextPopoverProps {
  currentValue: string;
  onApply: (value: string) => void;
  onClose?: () => void;
  placeholder?: string;
  // When true, hides the Apply bar and commits on every keystroke.
  // Enter still closes the popover.
  applyOnChange?: boolean;
  // Wraps each onApply invocation. Use with applyOnChange to debounce or
  // schedule the commit (e.g. useStableDebouncedCallback + startTransition).
  defer?: (apply: () => void) => void;
  // Applied to the underlying text input element.
  dataTestId?: string;
}

export function Select2TextPopover({
  currentValue,
  onApply,
  onClose,
  placeholder,
  applyOnChange = false,
  defer,
  dataTestId,
}: Select2TextPopoverProps) {
  const [input, setInput] = useState(currentValue);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const commit = (value: string) => {
    const trimmed = value.trim();
    const apply = () => onApply(trimmed);
    if (defer) {
      defer(apply);
    } else {
      apply();
    }
  };

  const commitAndClose = () => {
    commit(input);
    onClose?.();
  };

  return (
    <Stack sx={{ minWidth: 240 }}>
      <TextField
        size="small"
        inputRef={inputRef}
        value={input}
        onChange={(e) => {
          const next = e.target.value;
          setInput(next);
          if (applyOnChange) {
            commit(next);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitAndClose();
          }
        }}
        placeholder={placeholder}
        slotProps={{
          htmlInput: dataTestId ? { "data-testid": dataTestId } : undefined,
        }}
      />
      {!applyOnChange && <Select2ApplyBar onApply={commitAndClose} />}
    </Stack>
  );
}
